import {Container, Stack, alpha, useTheme} from '@mui/system';
import React from 'react'
import {Typography, useMediaQuery,} from '@mui/material'
import {MUIIcon} from '../../../../utility/component/Icon';
import Line from '../../../../utility/component/Line/Line';
import List from '../../../../utility/component/List/List';
import ImageBackground, {ImageT} from "../../../../utility/component/ImageBackground";

type ItemT = {
  title?: string,
  height?: number,
  description?:
    {
      title: string,
      content: string[]
    }[],
  bottomLogo?: ImageT[],
  subTitle?: string,
  caption?: string,
}

const CourseTimeline = ({data, embedded}: { data: ItemT[]; embedded?: boolean }) => {
  const theme = useTheme();
  const color = theme.palette;

  return (
    <Stack
      sx={{
        width: 1,
        pt: embedded ? 0 : 12,
        pb: embedded ? 0 : 8,
        bgcolor: embedded ? "transparent" : alpha(color.primary.dark, .7),
      }}>
      <Container
        disableGutters
        maxWidth={false}
      >
        <Stack direction="row">
          <TimelineContainer data={data} embedded={embedded}/>
        </Stack>
      </Container>
    </Stack>
  )
}

const TimelineContainer = ({data, embedded}: { data: ItemT[]; embedded?: boolean }) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('lg'));

  return (
    <Stack
      direction={matches ? "column" : "row"}
      justifyContent="center"
      width={1}
      m={embedded ? 0 : 1}
      spacing={embedded ? { xs: 2.5, md: 3 } : 0}
      alignItems={embedded && !matches ? "stretch" : "center"}>
      {data.map((item, i) =>
        <Stack key={i} width={1} direction={matches ? "column" : "row"} flexWrap={matches ? "wrap" : "nowrap"}
               alignItems={embedded && !matches ? "stretch" : "center"} justifyContent="center">
          {i !== 0 && (
            <Stack
              justifyContent="center"
              alignItems="center"
              sx={{
                height: embedded && !matches ? 1 : "auto",
                flexShrink: 0,
              }}>
              <MUIIcon name={matches ? "Reply" : "Reply"} sx={{
                color: embedded ? alpha(theme.palette.primary.contrastText, .28) : "primary.light",
                fontSize: embedded ? 32 : 46,
                m: embedded ? .5 : 1,
                transform: matches ? "scaleX(-1) rotate(-90deg)" : "scaleX(-1)"
              }}/>
            </Stack>
          )}
          <Item {...item} embedded={embedded} />
        </Stack>
      )}
    </Stack>
  )
}

const Item = ({title, subTitle, caption, description, bottomLogo, height, embedded}: ItemT & { embedded?: boolean }) => {
  const theme = useTheme();
  const color = theme.palette;

  return (
    <Stack
      direction="row"
      width={embedded ? 1 : {xl: 400, lg: 340, sm: 460, xs: 300}}
      sx={
        embedded
          ? {
              maxWidth: "var(--courseTimelineItemMaxWidth, none)",
            }
          : undefined
      }
      minHeight={embedded ? { xs: 440, md: height ?? 630 } : height ?? 630}
      height={embedded ? { xs: "auto", md: 1 } : "auto"}
      flex={1}>
      <Stack p={3} spacing={1} flex={1}
             bgcolor={embedded ? undefined : alpha(color.primary.main, .7)}
             borderRadius={0}
             boxShadow={embedded ? undefined : 20}
             sx={{
               border: embedded ? `1px solid ${alpha(color.primary.contrastText, .28)}` : undefined,
               background: embedded
                 ? `linear-gradient(145deg, ${alpha(color.primary.main, .7)} 50%, ${alpha(color.primary.light, .2)} 100%)`
                 : undefined,
               backdropFilter: embedded ? "blur(14px)" : undefined,
               boxShadow: embedded ? `0 18px 54px ${alpha(color.primary.dark, .26)}` : undefined,
             }}
             justifyContent="space-between">
        <Stack>
          <Stack alignItems="center" spacing={1.5}>
            <Stack alignItems="center" width={1} spacing={1}>
              <Typography variant="h4" color={embedded ? alpha(color.info.light, .9) : alpha(color.secondary.hyper, .8)}
                          fontWeight="bold">{title}</Typography>
              <Line width={1} height={2} color={embedded ? alpha(color.info.main, .7) : color.primary.main}/>
            </Stack>
            <Stack alignItems="center" width={1} spacing={1}>
              <Typography variant="h5" color={alpha(color.primary.contrastText, .86)}
                          sx={{textDecoration: embedded ? "none" : 'underline'}}>{subTitle}</Typography>
              <Typography variant="body2" color={alpha(color.primary.contrastText, .6)} fontWeight="light"
                          fontStyle="unset">{caption}</Typography>
            </Stack>
          </Stack>
          <Stack spacing={3} mt={3} mb={6}>
            {
              description?.map((item: any, i: number) =>
                <Stack key={i} spacing={.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <MUIIcon name="FiberManualRecord"
                             sx={{color: embedded ? alpha(color.info.light, .9) : alpha(color.third.light, 1), fontSize: 12}}/>
                    <Typography color={alpha(color.primary.contrastText, .82)} fontWeight="bold">{item.title}</Typography>
                  </Stack>
                  <Container>
                    <List list={item.content} color={alpha(color.primary.contrastText, .78)}
                          dotColor={embedded ? alpha(color.info.light, .9) : alpha(color.third.light, 1)}
                          spacing={.5}/>
                  </Container>
                </Stack>
              )
            }
          </Stack>
        </Stack>
        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" flexWrap="wrap">
          {
            bottomLogo?.map((item, i) =>
              <ImageBackground key={i} {...item}/>)
          }
        </Stack>
      </Stack>
    </Stack>
  )
}

export default CourseTimeline
